/**
 * bulkUpload — the carga-masiva upload engine.
 *
 * Uploads N expedientes (from folderScan) through the existing frozen contract:
 * per expediente, `signUploads` mints one opaque URL per file, then each File
 * is PUT directly to storage. Design constraints:
 *
 * - Signed PUT URLs expire in 15 minutes (storage_gcs.py `_UPLOAD_URL_TTL`), so
 *   signing is JUST-IN-TIME per expediente inside the worker pool — never
 *   pre-signed for the whole batch — and every retry re-signs from scratch.
 * - Bounded concurrency (default 4 expedientes in flight; each PUTs its ≤4
 *   files in parallel). Thousands of items only ever hold File HANDLES in
 *   memory, never bytes.
 * - Retries: an expediente is atomic — any failed file fails the attempt and
 *   the whole expediente re-signs + re-PUTs (objects overwrite idempotently).
 *   Exponential backoff with jitter between attempts.
 * - A fatal error (auth) aborts the whole run instead of failing 2,000 times.
 *
 * `sign`/`put` are injected so tests and DEMO mode swap them without touching
 * the engine.
 */

import type { BulkFile } from "./folderScan";

export type BulkItem = {
  expedienteId: string;
  files: BulkFile[];
};

export type SignedUploadLite = { tipo: string; upload_url: string };

export type SignFn = (
  expedienteId: string,
  files: { tipo: string; content_type: string }[],
) => Promise<{ uploads: SignedUploadLite[] }>;

export type PutFn = (
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
) => Promise<void>;

export type BulkProgress = {
  total: number;
  done: number;
  failed: number;
  inFlight: number;
  bytesTotal: number;
  /** Completed files' bytes plus the in-flight partials (smooth bar). */
  bytesDone: number;
};

export type ItemOutcome = {
  expedienteId: string;
  ok: boolean;
  attempts: number;
  /** Spanish, operator-facing. Present when !ok. */
  error?: string;
};

export type BulkUploadOptions = {
  sign: SignFn;
  put: PutFn;
  signal?: AbortSignal;
  /** Expedientes in flight at once. */
  concurrency?: number;
  /** Attempts per expediente (1 = no retry). */
  maxAttempts?: number;
  /** True ⇒ stop the whole run (e.g. auth expired); the error is not retried. */
  isFatal?: (err: unknown) => boolean;
  onProgress?: (p: BulkProgress) => void;
  /** Throttle for onProgress, ms. Completion events always emit. */
  progressIntervalMs?: number;
};

const DEFAULT_CONCURRENCY = 4;
const DEFAULT_MAX_ATTEMPTS = 3;
const BACKOFF_BASE_MS = 1_000;

export const CANCELLED_MESSAGE = "Cancelado por el operador.";

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "Error desconocido durante la carga.";
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const id = setTimeout(done, ms);
    function done() {
      signal?.removeEventListener("abort", done);
      clearTimeout(id);
      resolve();
    }
    signal?.addEventListener("abort", done, { once: true });
  });
}

/**
 * Runs the whole batch; resolves with one outcome per item, in input order.
 * Never rejects for per-item failures — only a thrown fatal error from the
 * caller's own callbacks would propagate.
 */
export async function runBulkUpload(
  items: BulkItem[],
  options: BulkUploadOptions,
): Promise<ItemOutcome[]> {
  const {
    sign,
    put,
    signal,
    concurrency = DEFAULT_CONCURRENCY,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    isFatal = () => false,
    onProgress,
    progressIntervalMs = 150,
  } = options;

  const outcomes: ItemOutcome[] = items.map((it) => ({
    expedienteId: it.expedienteId,
    ok: false,
    attempts: 0,
  }));

  const bytesTotal = items.reduce(
    (n, it) => n + it.files.reduce((m, f) => m + f.file.size, 0),
    0,
  );
  let bytesCompleted = 0;
  let done = 0;
  let failed = 0;
  let inFlight = 0;
  /** expediente index → bytes of its in-flight partial progress. */
  const partials = new Map<number, number>();

  let fatalStop = false;
  let lastEmit = 0;
  function emit(force: boolean): void {
    const now = Date.now();
    if (!force && now - lastEmit < progressIntervalMs) return;
    lastEmit = now;
    let partial = 0;
    for (const v of partials.values()) partial += v;
    onProgress?.({
      total: items.length,
      done,
      failed,
      inFlight,
      bytesTotal,
      bytesDone: Math.min(bytesTotal, bytesCompleted + partial),
    });
  }

  async function attemptItem(index: number): Promise<void> {
    const item = items[index];
    const specs = item.files.map((f) => ({ tipo: f.kind, content_type: f.contentType }));
    const signed = await sign(item.expedienteId, specs);
    const urlByTipo = new Map(signed.uploads.map((u) => [u.tipo, u.upload_url]));

    const fileBytes = new Map<string, number>();
    function reportPartial(): void {
      let sum = 0;
      for (const v of fileBytes.values()) sum += v;
      partials.set(index, sum);
      emit(false);
    }

    await Promise.all(
      item.files.map(async (f) => {
        const url = urlByTipo.get(f.kind);
        if (!url) {
          throw new Error(`La firma no devolvió URL para "${f.kind}".`);
        }
        await put(
          url,
          f.file,
          f.contentType,
          (pct) => {
            fileBytes.set(f.kind, Math.round((pct / 100) * f.file.size));
            reportPartial();
          },
          signal,
        );
        fileBytes.set(f.kind, f.file.size);
        reportPartial();
      }),
    );
  }

  let cursor = 0;
  async function worker(): Promise<void> {
    while (cursor < items.length) {
      if (signal?.aborted || fatalStop) return;
      const index = cursor++;
      const outcome = outcomes[index];
      inFlight++;
      emit(true);
      try {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          outcome.attempts = attempt;
          try {
            await attemptItem(index);
            outcome.ok = true;
            outcome.error = undefined;
            break;
          } catch (err) {
            outcome.error = errorMessage(err);
            if (signal?.aborted) {
              outcome.error = CANCELLED_MESSAGE;
              break;
            }
            if (isFatal(err)) {
              fatalStop = true;
              break;
            }
            if (attempt < maxAttempts) {
              const backoff = BACKOFF_BASE_MS * 4 ** (attempt - 1) + Math.random() * 500;
              await sleep(backoff, signal);
              // A cancel lands here by resolving the sleep early — stop, don't
              // burn another sign+PUT cycle against an aborted signal.
              if (signal?.aborted) {
                outcome.error = CANCELLED_MESSAGE;
                break;
              }
            }
          }
        }
      } finally {
        partials.delete(index);
        inFlight--;
        if (outcome.ok) {
          done++;
          bytesCompleted += items[index].files.reduce((n, f) => n + f.file.size, 0);
        } else {
          failed++;
        }
        emit(true);
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => worker()),
  );

  // Items never started (cancelled or fatal stop) get an explicit outcome.
  for (const outcome of outcomes) {
    if (!outcome.ok && outcome.attempts === 0) {
      outcome.error = CANCELLED_MESSAGE;
    }
  }
  emit(true);
  return outcomes;
}
